import React, { useEffect, useState } from "react";
import { Wallet, TrendingUp, Calendar, ArrowUpRight, ArrowDownLeft, Lock, Percent } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { formatINR, formatDate } from "../../lib/format";
import { toast } from "sonner";

function WalletStat({ icon: Icon, label, value, color }) {
  const colors = {
    emerald: "bg-emerald-50 text-emerald-600",
    indigo: "bg-[#FFF4ED] text-[#F97316]",
    amber: "bg-amber-50 text-amber-600",
  };
  return (
    <div className="card-base p-6">
      <div className="flex items-center gap-3">
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${colors[color]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="text-sm text-gray-500">{label}</div>
      </div>
      <div className="mt-3 text-3xl font-bold font-display">{value}</div>
    </div>
  );
}

function WithdrawModal({ open, onClose, balance, onSuccess }) {
  const [upi, setUpi] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amountPaise = Math.round(parseFloat(amount || "0") * 100);
    if (!upi || !upi.includes("@")) {
      toast.error("Enter a valid UPI ID like name@upi.");
      return;
    }
    if (!amountPaise || amountPaise <= 0 || amountPaise > balance) {
      toast.error(`Enter an amount up to ${formatINR(balance)}.`);
      return;
    }
    setBusy(true);
    try {
      await api.post("/tenant/wallet/withdraw", { upi_id: upi, amount_paise: amountPaise });
      toast.success(`${formatINR(amountPaise)} sent to ${upi}!`);
      setUpi(""); setAmount("");
      onClose();
      onSuccess();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-6 space-y-4">
        <h2 className="text-lg font-semibold">Withdraw Funds</h2>
        <p className="text-sm text-gray-500">Available balance: {formatINR(balance)}</p>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-xs font-medium text-gray-700 block mb-1">Amount *</label>
            <div className="relative">
              <span className="absolute left-3 top-2.5 text-gray-500 font-medium">₹</span>
              <input
                type="number"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full border border-gray-200 rounded-lg pl-8 pr-3 py-2 text-sm"
                step="0.01"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700 block mb-1">UPI ID *</label>
            <input
              type="text"
              placeholder="yourname@upi"
              value={upi}
              onChange={(e) => setUpi(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} disabled={busy} className="flex-1 border border-gray-300 text-gray-700 rounded-lg py-2 text-sm font-medium hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={busy} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg py-2 text-sm font-medium disabled:opacity-50">
              {busy ? "Processing..." : "Withdraw"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DepositInterestSection() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data: rentals } = await api.get("/rentals");
        const protectedRentals = (rentals || []).filter((r) => r.deposit_protection);
        const results = await Promise.all(
          protectedRentals.map(async (r) => {
            try {
              const { data } = await api.get(`/tenant/wallet/interest/${r.id}`);
              return { rental: r, interest: data };
            } catch {
              return null;
            }
          })
        );
        setRows(results.filter((x) => x && x.interest.current_cycle));
      } catch (e) {
        console.error("Failed to load deposit interest:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading || rows.length === 0) return null;

  return <TenantInterestCard rows={rows} />;
}

function TenantInterestCard({ rows }) {
  // Backend already redacts this to the caller's own share only — no other
  // party's amount (or Orello's cut) is ever included in the payload.
  const totalLocked = rows.reduce((sum, r) => sum + (r.interest.current_cycle?.my_locked_paise || 0), 0);

  return (
    <div className="card-base p-6 space-y-4">
      <div className="flex items-center gap-2">
        <Percent className="h-4 w-4 text-[#F97316]" />
        <h3 className="font-semibold">Deposit Interest</h3>
      </div>
      <p className="text-sm text-gray-500">
        Your 5% share of the 8% p.a. interest earned on the deposit Orello holds for you — accrues monthly, unlocks to your wallet every 6 months from the date your deposit was paid.
      </p>
      {totalLocked > 0 && (
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-800">
          <Lock className="h-4 w-4 shrink-0" />
          <span><span className="font-semibold">{formatINR(totalLocked)}</span> locked this cycle</span>
        </div>
      )}
      <div className="space-y-2">
        {rows.map(({ rental, interest }) => (
          <div key={rental.id} className="flex items-center justify-between p-3 border border-gray-100 rounded-lg text-sm">
            <div>
              <div className="font-medium text-gray-800">{rental.property_address}</div>
              <div className="text-xs text-gray-500">
                {interest.days_remaining_in_cycle != null
                  ? `Unlocks in ${interest.days_remaining_in_cycle} day${interest.days_remaining_in_cycle === 1 ? "" : "s"}`
                  : "—"}
              </div>
            </div>
            <div className="font-semibold text-amber-700">{formatINR(interest.current_cycle?.my_locked_paise || 0)}</div>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-gray-400">
        Any applicable platform fee or GST will be deducted at the time of withdrawal, if applicable.
      </p>
    </div>
  );
}

export default function TenantWallet() {
  const [w, setW] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tenant/wallet");
      setW(data);
    } catch (e) {
      console.error("Failed to load tenant wallet:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const balance = w?.balance_paise || 0;
  const totalEarned = w?.total_earned_paise || 0;
  const totalWithdrawn = w?.total_withdrawn_paise || 0;
  const locked = w?.locked_balance_paise || 0;
  const transactions = w?.transactions || [];

  const typeLabel = (type) => {
    if (type === "deposit_interest") return "Deposit Interest";
    if (type === "withdrawal_debit") return "Withdrawal";
    return type;
  };

  return (
    <div className="space-y-6" data-testid="tenant-wallet">
      <div>
        <h1 className="text-3xl font-bold font-display">Orello Wallet</h1>
        <p className="text-sm text-gray-500 mt-1">Your deposit interest earnings.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <WalletStat icon={Wallet} label="Available Balance" value={formatINR(balance)} color="emerald" />
        <WalletStat icon={TrendingUp} label="Total Earned" value={formatINR(totalEarned)} color="indigo" />
        <WalletStat icon={Calendar} label="Total Withdrawn" value={formatINR(totalWithdrawn)} color="amber" />
      </div>

      {locked > 0 && (
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
          <Lock className="h-4 w-4 shrink-0" />
          <span><span className="font-semibold">{formatINR(locked)}</span> locked — unlocks once your current interest cycle ends</span>
        </div>
      )}

      {balance > 0 && (
        <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 border border-emerald-200 rounded-xl p-6 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-emerald-900">Ready to withdraw?</h3>
            <p className="text-sm text-emerald-700 mt-1">You have {formatINR(balance)} available to withdraw anytime.</p>
          </div>
          <button
            onClick={() => setShowWithdrawModal(true)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2 rounded-lg font-medium whitespace-nowrap"
          >
            Withdraw Funds
          </button>
        </div>
      )}

      <DepositInterestSection />

      <div className="card-base p-6">
        <h3 className="font-semibold mb-4">Transaction History</h3>
        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : transactions.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p>No transactions yet.</p>
            <p className="text-sm mt-1">Interest earned on your deposit will appear here.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {transactions.map((txn) => (
              <div key={txn.id} className="flex items-center justify-between p-4 border border-gray-100 rounded-lg hover:bg-gray-50 transition">
                <div className="flex items-center gap-3">
                  <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                    txn.type.includes("credit") || txn.type === "deposit_interest" ? "bg-emerald-100 text-emerald-600" : "bg-amber-100 text-amber-600"
                  }`}>
                    {txn.type.includes("credit") || txn.type === "deposit_interest" ? <ArrowDownLeft className="h-5 w-5" /> : <ArrowUpRight className="h-5 w-5" />}
                  </div>
                  <div>
                    <div className="font-medium text-sm text-gray-800">{typeLabel(txn.type)}</div>
                    <div className="text-xs text-gray-500">{txn.property_address || txn.description || formatDate(txn.created_at)}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`font-semibold ${txn.type === "withdrawal_debit" ? "text-amber-600" : "text-emerald-600"}`}>
                    {txn.type === "withdrawal_debit" ? "-" : "+"}{formatINR(txn.amount_paise)}
                  </div>
                  <div className="text-xs text-gray-500 capitalize">{txn.status}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <WithdrawModal
        open={showWithdrawModal}
        onClose={() => setShowWithdrawModal(false)}
        balance={balance}
        onSuccess={load}
      />
    </div>
  );
}
